import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import browser360 from 'assets/v3/extension/browser-360.webp';
import browserEdge from 'assets/v3/extension/browser-edge.svg';
import browserFirefox from 'assets/v3/extension/browser-firefox.svg';
import browserSafari from 'assets/v3/extension/browser-safari.svg';
import chromeIcon from 'assets/v3/extension/chrome.svg';
import feature1 from 'assets/v3/extension/feature-1.webp';
import feature2 from 'assets/v3/extension/feature-2.webp';
import offlineIcon from 'assets/v3/extension/offline.svg';

import {PageHero, PageShell, PageTitle, Section} from 'components';
import Icon from 'components/v3/Icon';
import {ROUTES} from 'constants/routes';

const LISTING_ICON_PATHS = [
  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
];
const MARKETING_ICON_PATHS = [
  'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
];

/**
 * 浏览器插件落地页：迁移自独立部署的 www.xiangjifanyi.com/extension（Nuxt SPA，2026-09）。
 * 内容：H1/副标题 + 6 个浏览器下载卡（Chrome/360/Safari 即将上线/Firefox/Edge/离线安装）
 * + 2 个图文交替特性卡（一键翻译商品详情页/图层编辑精修）+「本地化内容与营销素材生成」
 * 图标卡区块。资产在 src/assets/v3/extension/。
 * 与原站的差异：老页头/老页脚弃用（本站 v3 TopNav/Footer）；「离线安装」指向站内
 * 使用指南的浏览器插件 tab（/guide?tab=extension）。
 * 2026-09 v3 页面统一改版：套 PageHero(light)/Section 骨架 + 玻璃卡，与其余 12 页同风格。
 * 2026-09-17：定位从单一图片翻译扩展为 AI 电商助手，标题/副标题改写；去掉「翻译前后
 * 效果对比」特性卡（业务要求精简）；新增本地化 Listing/营销素材生成能力区块（该能力
 * 上线较新无历史截图，采用图标卡样式，不虚构界面截图）。
 */

interface BrowserCard {
  /** 站内路由（离线安装 → 使用指南插件 tab） */
  internalTo?: string;
  /** 站外应用商店链接 */
  href?: string;
  icon: string;
  /** 浏览器品牌名（不翻译）或词典键（离线安装） */
  label: string;
  /** 悬停提示的词典键 */
  tipKey?: string;
}

interface FeatureCard {
  altKey: string;
  descKey: string;
  image: string;
  /** 文右图左（false=图左文右） */
  reversed?: boolean;
  titleKey: string;
}

interface CapabilityCard {
  descKey: string;
  iconPaths: string[];
  titleKey: string;
}

const Extension: FC = () => {
  const {t} = useTranslation('products');

  const browserCards: BrowserCard[] = [
    {
      href: 'https://chromewebstore.google.com/detail/%E8%B1%A1%E5%AF%84%E5%9B%BE%E7%89%87%E7%BF%BB%E8%AF%91%E5%8A%A9%E6%89%8B/ejkpocbmomaefkinmkfbfnpjjfbinegb',
      icon: chromeIcon,
      label: 'Chrome',
    },
    {
      href: 'https://ext.se.360.cn/#/extension-detail?id=mghdbkplccibimngjbnahopdbpnppmlj',
      icon: browser360,
      label: '360',
      tipKey: 'extension.browsers.browser360Tip',
    },
    {icon: browserSafari, label: 'Safari', tipKey: 'extension.browsers.safariTip'},
    {
      href: 'https://addons.mozilla.org/zh-CN/firefox/addon/%E8%B1%A1%E5%AF%84%E5%9B%BE%E7%89%87%E7%BF%BB%E8%AF%91%E5%8A%A9%E6%89%8B/',
      icon: browserFirefox,
      label: 'Firefox',
    },
    {
      href: 'https://microsoftedge.microsoft.com/addons/detail/%E8%B1%A1%E5%AF%84%E5%9B%BE%E7%89%87%E7%BF%BB%E8%AF%91%E5%8A%A9%E6%89%8B/flafdloimcmfnccgoahbmmncgiifheea',
      icon: browserEdge,
      label: 'Edge',
    },
    {icon: offlineIcon, internalTo: `${ROUTES.guide}?tab=extension`, label: t('extension.browsers.offline')},
  ];

  const featureCards: FeatureCard[] = [
    {
      altKey: 'extension.features.translate.imageAlt',
      descKey: 'extension.features.translate.desc',
      image: feature1,
      titleKey: 'extension.features.translate.title',
    },
    {
      altKey: 'extension.features.retouch.imageAlt',
      descKey: 'extension.features.retouch.desc',
      image: feature2,
      reversed: true,
      titleKey: 'extension.features.retouch.title',
    },
  ];

  const capabilityCards: CapabilityCard[] = [
    {
      descKey: 'extension.features.listing.desc',
      iconPaths: LISTING_ICON_PATHS,
      titleKey: 'extension.features.listing.title',
    },
    {
      descKey: 'extension.features.marketingAssets.desc',
      iconPaths: MARKETING_ICON_PATHS,
      titleKey: 'extension.features.marketingAssets.title',
    },
  ];

  const renderCapabilityCard = (card: CapabilityCard) => (
    <div className="glass-card rounded-2xl p-6 lg:p-7" key={card.titleKey}>
      <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center mb-5">
        <Icon className="w-6 h-6 text-indigo-600" paths={card.iconPaths} />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t(card.titleKey)}</h3>
      <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t(card.descKey)}</p>
    </div>
  );

  /** 悬停提示气泡（360/Safari 卡） */
  const renderTip = (tipKey?: string) =>
    tipKey ? (
      <div className="absolute bottom-full mb-2 hidden group-hover:block bg-slate-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
        {t(tipKey)}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
      </div>
    ) : null;

  const renderBrowserCard = (card: BrowserCard) => {
    const inner = (
      <div className="glass-card rounded-2xl px-6 py-5 flex flex-col items-center gap-3 relative group transition-transform duration-300 hover:-translate-y-1 w-28">
        <img
          alt={t('extension.browsers.iconAlt', {name: card.label})}
          className="h-12 w-12"
          decoding="async"
          src={card.icon}
        />
        <span className="text-sm font-medium text-slate-600">{card.label}</span>
        {renderTip(card.tipKey)}
      </div>
    );
    if (card.internalTo) {
      return (
        <Link className="no-underline" key={card.label} to={card.internalTo}>
          {inner}
        </Link>
      );
    }
    if (card.href) {
      return (
        <a className="no-underline" href={card.href} key={card.label} rel="noreferrer" target="_blank">
          {inner}
        </a>
      );
    }
    return <div key={card.label}>{inner}</div>;
  };

  return (
    <PageShell>
      <PageTitle
        canonicalUrl="https://www.xiangjifanyi.com/home/extension"
        description={t('extension.seo.description')}
        ogDescription={t('extension.seo.ogDescription')}
        title={t('extension.seo.title')}
      />
      <PageHero description={t('extension.subtitle')} title={t('extension.title')} variant="light" />
      <Section className="pt-0 lg:pt-0">
        <div className="flex flex-wrap justify-center gap-4 relative z-10">{browserCards.map(renderBrowserCard)}</div>
      </Section>
      <Section bg="muted">
        <div className="space-y-16 lg:space-y-24">
          {featureCards.map((card) => (
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" key={card.titleKey}>
              <img
                alt={t(card.altKey)}
                className={`w-full rounded-2xl shadow-xl ${card.reversed ? 'lg:order-2' : ''}`}
                decoding="async"
                loading="lazy"
                src={card.image}
              />
              <div className={card.reversed ? 'lg:order-1' : ''}>
                <h2 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight mb-4">
                  {t(card.titleKey)}
                </h2>
                <p className="text-slate-500 lg:text-lg leading-relaxed">{t(card.descKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section title={t('extension.capabilitiesTitle')}>
        <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">{capabilityCards.map(renderCapabilityCard)}</div>
      </Section>
    </PageShell>
  );
};

export default Extension;
